import { useState } from "react";
import { ArrowDown, ArrowRight, Check, FileHtml, LockKey, Plus, Sparkle, UploadSimple, X } from "@phosphor-icons/react";
import type { PhotoFolioProject, TemplateId } from "../domain/project";
import { DEPTH_SHOWCASE_DOCUMENT, SHOWCASE_DOCUMENT, SHOWCASE_URLS, type DemoCollection } from "./home-showcase-data";
import { CircularGalleryPrototype } from "./CircularGalleryPrototype";
import { DepthPhotoGalleryPrototype } from "./DepthPhotoGalleryPrototype";
import { HomeTemplateShowcase } from "./HomeTemplateShowcase";
import { PointerTilt, Reveal } from "./HomeMotion";
import { PhotoFrame } from "./PhotoFrame";
import { SafeImage } from "./SafeImage";
import { useAssetUrls } from "../hooks/useAssetUrls";

const showcaseBase = `${import.meta.env.BASE_URL}showcase/`;
export const RECENT_ALBUM_LIMIT = 5;

export function selectRecentProjects(projects: PhotoFolioProject[]) {
  return projects.slice(0, RECENT_ALBUM_LIMIT);
}

export function getRecentCoverVariant(featured: boolean): "preview" | "thumbnail" {
  return featured ? "preview" : "thumbnail";
}

interface HomeProps {
  projects: PhotoFolioProject[];
  onCreate: (details: { title: string; subtitle: string; date: string; location: string; people: string[] }) => void;
  onOpen: (project: PhotoFolioProject) => void;
  onImport: (file: File) => void;
  onDemo: (template?: TemplateId, collection?: DemoCollection) => void;
}

function RecentAlbumCard({ project, index, featured, onOpen }: { project: PhotoFolioProject; index: number; featured: boolean; onOpen: (project: PhotoFolioProject) => void }) {
  const coverItem = project.items.find((item) => item.assetId === project.coverAssetId) ?? project.items[0];
  const urls = useAssetUrls(project.id, coverItem ? [coverItem] : [], getRecentCoverVariant(featured));
  const coverUrl = coverItem ? urls[coverItem.assetId] : undefined;
  const details = [project.date, project.location].filter(Boolean).join(" · ");
  return (
    <button className={`recent-album-card${featured ? " featured" : ""}`} type="button" onClick={() => onOpen(project)} aria-label={`继续编辑影集：${project.title || "未命名影集"}`}>
      <span className="recent-album-cover" aria-hidden="true">
        <PhotoFrame
          src={coverUrl}
          alt=""
          className="recent-album-media"
          presentation={{ fit: "contain", frame: "none", spacing: "compact" }}
          loading={featured ? "eager" : "lazy"}
          fetchPriority={featured ? "high" : "auto"}
          decoding="async"
          placeholder={<span className="recent-album-fallback"><strong>PF</strong><small>等待封面</small></span>}
        />
      </span>
      <span className="recent-album-wash" aria-hidden="true" />
      <span className="recent-album-content">
        <span className="recent-album-kicker">{featured ? "最近打开" : `影集 ${String(index + 1).padStart(2, "0")}`}</span>
        <strong className="recent-album-title">{project.title || "未命名影集"}</strong>
        <span className="recent-album-subtitle">{project.subtitle || "把经过的光，留在这里。"}</span>
        <span className="recent-album-meta"><span>{project.items.length} 张照片</span>{details ? <span>{details}</span> : null}</span>
        <span className="recent-album-open">继续编辑 <ArrowRight size={17} /></span>
      </span>
    </button>
  );
}

export function Home({ projects, onCreate, onOpen, onImport, onDemo }: HomeProps) {
  const [creating, setCreating] = useState(false);
  const [details, setDetails] = useState({ title: "", subtitle: "", date: "", location: "", people: "" });
  const confirmCreate = () => {
    onCreate({
      title: details.title.trim() || "未命名影集",
      subtitle: details.subtitle.trim() || "把经过的光，留在这里。",
      date: details.date,
      location: details.location.trim(),
      people: details.people.split(/[，,]/).map((item) => item.trim()).filter(Boolean),
    });
    setCreating(false);
  };
  const recentProjects = selectRecentProjects(projects);
  return (
    <main className="home-shell">
      <nav className="home-nav">
        <a className="brand" href="#top" aria-label="PhotoFolio 首页"><span className="brand-mark">PF</span><span>PhotoFolio</span></a>
        <label className="text-button file-button"><UploadSimple size={18} />导入草稿
          <input type="file" accept=".photofolio,.zip" onChange={(event) => event.target.files?.[0] && onImport(event.target.files[0])} />
        </label>
      </nav>

      <section className="home-hero" id="top">
        <Reveal className="hero-copy">
          <span className="eyebrow">本地优先的影集工作台</span>
          <h1><span>照片离开云端，</span><span>故事留在手边。</span></h1>
          <p>整理照片，写下记忆，导出一个断网也能阅读的完整网页。所有素材都在你的浏览器里完成处理。</p>
          <div className="hero-actions">
            <button className="primary-button" type="button" onClick={() => setCreating(true)}><Plus size={19} weight="bold" />开始整理照片</button>
            <button className="secondary-button" type="button" onClick={() => onDemo()}><Sparkle size={19} />打开示例影集</button>
            <a className="hero-scroll-link" href="#reading-modes"><span>先看看阅读方式</span><ArrowDown size={16} /></a>
          </div>
          <div className="home-principles" aria-label="PhotoFolio 特点">
            <div className="home-principle"><LockKey size={17} /><span><strong>不上传</strong>照片只在本机处理</span></div>
            <div className="home-principle"><FileHtml size={17} /><span><strong>可带走</strong>导出独立 HTML</span></div>
            <div className="home-principle"><Check size={17} /><span><strong>可继续</strong>项目自动保存</span></div>
          </div>
        </Reveal>
        <Reveal className="hero-visual" delay={120} aria-label="PhotoFolio 真实照片预览">
          <PointerTilt className="home-photo-stack" maxTilt={3.2}>
            <div className="home-photo-card home-photo-card-back"><SafeImage src={`${showcaseBase}landscape-02.webp`} alt="夜空下的山脊" loading="lazy" decoding="async" /></div>
            <div className="home-photo-card home-photo-card-mid"><SafeImage src={`${showcaseBase}portrait-03.webp`} alt="在室内阅读的人" loading="lazy" decoding="async" /></div>
            <div className="home-photo-card home-photo-card-front"><SafeImage src={`${showcaseBase}landscape-01.webp`} alt="林间道路" decoding="async" fetchPriority="high" /><div className="home-photo-card-caption"><span>FIELD NOTE / 01</span><strong>把经过的光，留在这里。</strong></div></div>
            <span className="home-photo-note note-top">REAL PHOTOGRAPHS<br />LOCAL ONLY</span>
            <span className="home-photo-note note-bottom">SCROLL TO OPEN<br />THE ALBUM</span>
          </PointerTilt>
        </Reveal>
      </section>

      <Reveal className="project-section home-projects">
        <div className="section-title"><div><span className="eyebrow">继续未完成的故事</span><h2>最近的影集</h2></div><span>{projects.length} 个项目保存在此浏览器</span></div>
        {projects.length ? (
          <>
            <div className="recent-albums-grid">
              {recentProjects.map((project, index) => <RecentAlbumCard key={project.id} project={project} index={index} featured={index === 0} onOpen={onOpen} />)}
            </div>
            {projects.length > recentProjects.length ? <div className="recent-album-archive"><span>还有 {projects.length - recentProjects.length} 本影集</span>{projects.slice(recentProjects.length).map((project) => <button key={project.id} type="button" className="recent-archive-item" onClick={() => onOpen(project)}>{project.title || "未命名影集"}<ArrowRight size={15} /></button>)}</div> : null}
          </>
        ) : (
          <div className="empty-projects empty-projects-compact">
            <span><strong>还没有影集</strong>照片只会保存在你的浏览器中。</span>
            <button className="text-button" type="button" onClick={() => setCreating(true)}>创建第一本影集 <ArrowRight size={17} /></button>
          </div>
        )}
      </Reveal>

      <Reveal className="home-story-bridge" delay={40}>
        <div className="story-bridge-heading"><span className="eyebrow">从一堆照片开始</span><h2>照片不是终点，<br /><em>它们之间的关系才是。</em></h2></div>
        <div className="home-story-steps">
          <div className="home-story-step"><span>01</span><strong>选择照片</strong><p>拖进来，按你记得的顺序排好。</p></div>
          <div className="home-story-step"><span>02</span><strong>写下一句话</strong><p>标题、时间、地点，都可以慢慢补。</p></div>
          <div className="home-story-step"><span>03</span><strong>选择阅读方式</strong><p>翻页、放映、时间线，找到最像你的节奏。</p></div>
        </div>
      </Reveal>

      <HomeTemplateShowcase onOpenTemplate={onDemo} />

      <CircularGalleryPrototype document={SHOWCASE_DOCUMENT} urls={SHOWCASE_URLS} />

      <DepthPhotoGalleryPrototype document={DEPTH_SHOWCASE_DOCUMENT} urls={SHOWCASE_URLS} />

      <Reveal className="home-proof" delay={40}>
        <div className="home-proof-copy"><span className="eyebrow">你的照片，你的文件</span><h2>不需要账号，<br /><em>也能把成品带走。</em></h2><p>编辑过程保存在本机。完成后，导出一个自包含的 HTML 文件，发给朋友，或在很多年以后重新打开它。</p></div>
        <div className="home-proof-list">
          <div><span>01</span><strong>浏览器本地保存</strong><p>刷新页面也不会丢失正在整理的项目。</p></div>
          <div><span>02</span><strong>无外链依赖</strong><p>成品不请求 CDN、字体服务或 PhotoFolio 网站。</p></div>
          <div><span>03</span><strong>离线仍然可读</strong><p>双击 HTML，照片和文字一起出现。</p></div>
        </div>
      </Reveal>

      <Reveal className="home-final-cta" delay={50}>
        <span className="eyebrow">现在就开始</span><h2>给这段记忆<br />一个可以翻阅的形状。</h2>
        <div className="hero-actions"><button className="primary-button" type="button" onClick={() => setCreating(true)}><Plus size={19} weight="bold" />创建我的影集</button><button className="secondary-button" type="button" onClick={() => onDemo("book")}><Sparkle size={18} />看看真实相册</button></div>
      </Reveal>

      <footer className="home-footer"><span>PhotoFolio</span><span>不上传照片，不依赖账号。</span></footer>
      {creating ? <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setCreating(false)}>
        <section className="creation-dialog" role="dialog" aria-modal="true" aria-labelledby="creation-title">
          <div className="dialog-head"><div><span>创建一本新的影集</span><h2 id="creation-title">先给这段记忆一个名字</h2></div><button className="icon-button" type="button" aria-label="关闭" onClick={() => setCreating(false)}><X size={20} /></button></div>
          <div className="creation-fields">
            <label><span>相册名称</span><input autoFocus value={details.title} placeholder="例如：北海道的七个清晨" onChange={(event) => setDetails({ ...details, title: event.target.value })} /></label>
            <label><span>副标题，可稍后填写</span><input value={details.subtitle} placeholder="一句想写在封面上的话" onChange={(event) => setDetails({ ...details, subtitle: event.target.value })} /></label>
            <div className="field-pair"><label><span>日期</span><input type="date" value={details.date} onChange={(event) => setDetails({ ...details, date: event.target.value })} /></label><label><span>地点</span><input value={details.location} placeholder="例如：北海道" onChange={(event) => setDetails({ ...details, location: event.target.value })} /></label></div>
            <label><span>同行人物，可选</span><input value={details.people} placeholder="用逗号分隔姓名" onChange={(event) => setDetails({ ...details, people: event.target.value })} /></label>
          </div>
          <div className="dialog-note">照片只会在你的浏览器中处理。所有信息都可以稍后修改。</div>
          <div className="dialog-actions"><button className="secondary-button" type="button" onClick={() => setCreating(false)}>稍后再说</button><button className="primary-button" type="button" onClick={confirmCreate}>进入工作台 <ArrowRight size={18} /></button></div>
        </section>
      </div> : null}
    </main>
  );
}
